import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import ExportExcel from "../../components/ExportExcel";
import { toast } from "react-toastify";
import { monthsList } from "../../utils";
import { capitalizeWords, formatDate } from "../../functions";
import * as XLSX from "xlsx";
import { useDispatch, useSelector } from "react-redux";
import { fetchPaidFeeDetails } from "../../redux/paidFeeSlice";
import { server } from "../../server";

function PaidFee() {
  const dispatch = useDispatch();
  const {
    data: paidFeeDetails,
    loading,
    error,
  } = useSelector((state) => state.paidFee);

  useEffect(() => {
    dispatch(fetchPaidFeeDetails());
  }, [dispatch]);

  const monthList = [
    "All",
    "april",
    "may",
    "june",
    "july",
    "august",
    "september",
    "october",
    "november",
    "december",
    "january",
    "february",
    "march",
  ];

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const [selectedMonth, setSelectedMonth] = useState("All");
  const [selectedClass, setSelectedClass] = useState("All");
  const [selectedSection, setSelectedSection] = useState("All");
  const [selectedMonthForReport, setSelectedMonthForReport] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [downloading, setDownloading] = useState(false);

  const allClasses = [
    ...new Set(paidFeeDetails.map((s) => s.studentDetails.currentClass)),
  ];
  const allSections = [
    ...new Set(paidFeeDetails.map((s) => s.studentDetails.currentSection)),
  ];

  const scrollContainerRef = useRef(null);
  const scrollClassRef = useRef(null);
  const scrollSectionRef = useRef(null);

  const scrollLeft = () => {
    scrollContainerRef.current?.scrollBy({ left: -150, behavior: "smooth" });
  };

  const scrollRight = () => {
    scrollContainerRef.current?.scrollBy({ left: 150, behavior: "smooth" });
  };

  const scrollLeftClass = () => {
    if (scrollClassRef.current) scrollClassRef.current.scrollLeft -= 100;
  };

  const scrollRightClass = () => {
    if (scrollClassRef.current) scrollClassRef.current.scrollLeft += 100;
  };

  const scrollLeftSection = () => {
    if (scrollSectionRef.current) scrollSectionRef.current.scrollLeft -= 100;
  };

  const scrollRightSection = () => {
    if (scrollSectionRef.current) scrollSectionRef.current.scrollLeft += 100;
  };

  const filteredByAll = paidFeeDetails.filter((student) => {
    const matchesMonth =
      selectedMonth === "All" || student.paidMonths?.includes(selectedMonth);
    const matchesClass =
      selectedClass === "All" ||
      student.studentDetails.currentClass === selectedClass;
    const matchesSection =
      selectedSection === "All" ||
      student.studentDetails.currentSection === selectedSection;

    return matchesMonth && matchesClass && matchesSection;
  });

  // ✅ 1. First filter based on selected month
  const filteredByMonth = paidFeeDetails.filter((student) => {
    if (selectedMonth === "All") return true;
    return student.paidMonths?.includes(selectedMonth);
  });

  // ✅ 2. Then use that for search filtering
  const filteredRecords = filteredByAll.filter((scholarship) => {
    const searchLower = searchTerm.toLowerCase();
    return Object.values(scholarship).some((value) => {
      if (typeof value === "string") {
        return value.toLowerCase().includes(searchLower);
      } else if (typeof value === "object" && value !== null) {
        return Object.values(value).some(
          (subValue) =>
            typeof subValue === "string" &&
            subValue.toLowerCase().includes(searchLower),
        );
      }
      return false;
    });
  });

  const siblingStudents = filteredByAll.filter(
    (student) => student.scholarshipType === "sibling",
  );

  const totalDueOfSiblingStudents = siblingStudents.reduce((sum, student) => {
    const paidMonths = student.paidMonths || [];
    const monthlyFee = student.monthlyFee || 0;
    const transportFee = student.transportFee || 0;

    const count =
      selectedMonth === "All"
        ? paidMonths.length
        : paidMonths.includes(selectedMonth)
          ? 1
          : 0;
    const transportCount =
      selectedMonth === "June"
        ? 0
        : selectedMonth === "All"
          ? paidMonths.filter((m) => m !== "June").length
          : paidMonths.includes(selectedMonth)
            ? 1
            : 0;

    return sum + (monthlyFee * count + transportFee * transportCount);
  }, 0);

  let totalMonthlyDue = 0;
  let totalTransportDue = 0;

  filteredByAll.forEach((student) => {
    const paidMonths = student.paidMonths || [];
    const monthlyFee = student.monthlyFee || 0;
    const transportFee = student.transportFee || 0;

    const count =
      selectedMonth === "All"
        ? paidMonths.length
        : paidMonths.includes(selectedMonth)
          ? 1
          : 0;
    const transportCount =
      selectedMonth === "June"
        ? 0
        : selectedMonth === "All"
          ? paidMonths.filter((m) => m !== "June").length
          : paidMonths.includes(selectedMonth)
            ? 1
            : 0;

    totalMonthlyDue += monthlyFee * count;
    totalTransportDue += transportFee * transportCount;
  });

  const totalDueAllStudents = totalMonthlyDue + totalTransportDue;

  // ✅ Reset pagination when searching
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // ✅ Apply pagination AFTER filtering
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const columnMapping = {
    "studentDetails.studentId": "Student ID",
    "studentDetails.admission_no": "Admission No",
    "studentDetails.name": "Name",
    "studentDetails.currentClass": "Class",
    "studentDetails.currentSection": "Section",
    "studentDetails.rollNo": "Roll No",
    "studentDetails.phoneNo": "Phone No",
    "studentDetails.gender": "Gender",
    "transportFacility.busStop": "Bus Stop",
    "transportFacility.vehicleNo": "Vehicle No",
    "transportFacility.busNumber": "Bus No",
    scholarshipType: "Scholarship Type",
    paidMonths: "Paid Months",
    monthlyFee: "Monthly Fee",
    transportFee: "Transport Fee",
  };

  const handleExportExcel = () => {
    ExportExcel(paidFeeDetails, "ScholarshipDetails", formattedData);
  };

  const formattedData = paidFeeDetails.map((item) => ({
    "Admission No": item.studentDetails.admission_no,
    Name: item.studentDetails.name,
    Class: item.studentDetails.currentClass,
    Section: item.studentDetails.currentSection,
    "Roll No": item.studentDetails.rollNo,
    "Phone No": item.studentDetails.phoneNo,
    Gender: item.studentDetails.gender,
    "Scholarship Type": item.scholarshipType,
    "Paid Months": Array.isArray(item.paidMonths)
      ? item.paidMonths.join(", ")
      : "",
    "Monthly Fee": item.monthlyFee,
    "Transport Fee": item.transportFee,
  }));

  const getValueByPath = (obj, path) => {
    return path.split(".").reduce((acc, key) => {
      if (!acc) return "";
      const match = key.match(/(.*?)\[(\d+)\]/);
      if (match) {
        const arrayKey = match[1];
        const index = parseInt(match[2], 10);
        return acc[arrayKey] && acc[arrayKey][index] !== undefined
          ? acc[arrayKey][index]
          : "";
      }
      return acc[key] !== undefined ? acc[key] : "";
    }, obj);
  };

  const handleExportClassWiseExcel = () => {
    if (!columnMapping || Object.keys(columnMapping).length === 0) {
      alert("Column Missing");
      return;
    }

    const workbook = XLSX.utils.book_new();
    const today = new Date().toISOString().slice(0, 10);
    const classGroups = {};

    paidFeeDetails.forEach((student) => {
      const cls = student.studentDetails?.currentClass || "Unknown";
      const sec = student.studentDetails?.currentSection || "Unknown";

      if (!classGroups[cls]) {
        classGroups[cls] = {};
      }

      if (!classGroups[cls][sec]) {
        classGroups[cls][sec] = [];
      }

      const formattedItem = {};
      Object.entries(columnMapping).forEach(([key, header]) => {
        let value = getValueByPath(student, key);

        if (Array.isArray(value)) {
          value = value.join(", ");
        }

        if (key === "transportFacility.busStop" && typeof value === "string") {
          value = value.toUpperCase();
        }

        if (
          (key === "studentDetails.dateOfBirth" ||
            key === "studentDetails.admissionDate") &&
          value
        ) {
          value = formatDate(value);
        }

        formattedItem[header] = value;
      });

      classGroups[cls][sec].push(formattedItem);
    });

    Object.entries(classGroups).forEach(([className, sections]) => {
      const combinedData = [];
      const sortedSections = Object.keys(sections).sort();

      sortedSections.forEach((sec) => {
        const students = sections[sec];
        students.sort((a, b) => Number(a["Roll No"]) - Number(b["Roll No"]));
        combinedData.push({ "Student ID": `Section ${sec}` });
        combinedData.push(...students);
        combinedData.push({});
      });

      const worksheet = XLSX.utils.json_to_sheet(combinedData, {
        skipHeader: false,
      });
      XLSX.utils.book_append_sheet(workbook, worksheet, className.slice(0, 31));
    });

    XLSX.writeFile(workbook, `PaidFee_ClassWise_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportGenderWiseExcel = () => {
    if (!columnMapping || Object.keys(columnMapping).length === 0) {
      alert("Column Missing");
      return;
    }

    const workbook = XLSX.utils.book_new();
    const today = new Date().toISOString().slice(0, 10);
    const genderGroups = {};

    paidFeeDetails.forEach((student) => {
      const gender = student.studentDetails?.gender || "Unknown";

      if (!genderGroups[gender]) {
        genderGroups[gender] = [];
      }

      const formattedItem = {};
      Object.entries(columnMapping).forEach(([key, header]) => {
        let value = getValueByPath(student, key);

        if (Array.isArray(value)) {
          value = value.join(", ");
        }

        if (key === "transportFacility.busStop" && typeof value === "string") {
          value = value.toUpperCase();
        }

        if (
          (key === "studentDetails.dateOfBirth" ||
            key === "studentDetails.admissionDate") &&
          value
        ) {
          value = formatDate(value);
        }

        formattedItem[header] = value;
      });

      genderGroups[gender].push(formattedItem);
    });

    Object.entries(genderGroups).forEach(([gender, students]) => {
      students.sort((a, b) => Number(a["Roll No"]) - Number(b["Roll No"]));

      const worksheet = XLSX.utils.json_to_sheet(students, {
        skipHeader: false,
      });
      XLSX.utils.book_append_sheet(workbook, worksheet, gender.slice(0, 31));
    });

    XLSX.writeFile(workbook, `PaidFee_GenderWise_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportAvailingTransportWiseExcel = () => {
    if (!columnMapping || Object.keys(columnMapping).length === 0) {
      alert("Column Missing");
      return;
    }

    const workbook = XLSX.utils.book_new();
    const today = new Date().toISOString().slice(0, 10);
    const availingTransportList = [];

    paidFeeDetails.forEach((student) => {
      const busStop = student.transportFacility?.busStop;
      if (!busStop) return;

      const formattedItem = {};
      Object.entries(columnMapping).forEach(([key, header]) => {
        let value = getValueByPath(student, key);

        if (Array.isArray(value)) {
          value = value.join(", ");
        }

        if (key === "transportFacility.busStop" && typeof value === "string") {
          value = value.toUpperCase();
        }

        if (
          (key === "studentDetails.dateOfBirth" ||
            key === "studentDetails.admissionDate") &&
          value
        ) {
          value = formatDate(value);
        }

        formattedItem[header] = value;
      });

      availingTransportList.push(formattedItem);
    });

    if (availingTransportList.length === 0) {
      alert("No students found availing transport.");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(availingTransportList, {
      skipHeader: false,
    });

    XLSX.utils.book_append_sheet(workbook, worksheet, "Availing Transport");
    XLSX.writeFile(workbook, `PaidFee_AvailingTransport_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportClassMonthWiseExcel = (month) => {
    if (!columnMapping || Object.keys(columnMapping).length === 0) {
      alert("Column Missing");
      return;
    }

    if (!month || month === "All") {
      handleExportClassWiseExcel();
      return;
    }

    const workbook = XLSX.utils.book_new();
    const today = new Date().toISOString().slice(0, 10);
    const classGroups = {};

    // normalize
    const monthIndex = monthList.findIndex(
      (m) => m.toLowerCase() === month.toLowerCase(),
    );

    paidFeeDetails.forEach((student) => {
      const paidMonths = (student.paidMonths || []).map((m) => m.toLowerCase());

      // find latest paid month index
      const maxPaidIndex = Math.max(
        ...paidMonths.map((m) => monthList.indexOf(m)).filter((i) => i > 0),
        -1,
      );

      if (maxPaidIndex < monthIndex) return; // skip if not paid up to that month

      const cls = student.studentDetails?.currentClass || "Unknown";
      const sec = student.studentDetails?.currentSection || "Unknown";

      if (!classGroups[cls]) classGroups[cls] = {};
      if (!classGroups[cls][sec]) classGroups[cls][sec] = [];

      const formattedItem = {};
      Object.entries(columnMapping).forEach(([key, header]) => {
        let value = getValueByPath(student, key);
        if (Array.isArray(value)) value = value.join(", ");
        if (key === "transportFacility.busStop" && typeof value === "string") {
          value = value.toUpperCase();
        }
        if (
          (key === "studentDetails.dateOfBirth" ||
            key === "studentDetails.admissionDate") &&
          value
        ) {
          value = formatDate(value);
        }
        formattedItem[header] = value;
      });

      formattedItem["Paid Upto Month"] = month;
      classGroups[cls][sec].push(formattedItem);
    });

    if (Object.keys(classGroups).length === 0) {
      toast.error("No students found for this month");
      return;
    }

    Object.entries(classGroups).forEach(([className, sections]) => {
      const combinedData = [];
      const sortedSections = Object.keys(sections).sort();

      sortedSections.forEach((sec) => {
        const students = sections[sec];
        students.sort((a, b) => Number(a["Roll No"]) - Number(b["Roll No"]));
        combinedData.push({ "Student ID": `Section ${sec}` });
        combinedData.push(...students);
        combinedData.push({});
      });

      const worksheet = XLSX.utils.json_to_sheet(combinedData, {
        skipHeader: false,
      });
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        `${className}`.slice(0, 31),
      );
    });

    const closeButton = document.querySelector('[data-bs-dismiss="modal"]');
    if (closeButton) closeButton.click();

    XLSX.writeFile(workbook, `PaidFee_ClassMonthWise_${month}_${today}.xlsx`);
    toast.success("Report Generated");
  };

  const handleExportdateMonthWiseExcel = async (month, fromDate, toDate) => {
    try {
      setDownloading(true);

      const response = await fetch(
        `${server}/paid_fee_mongodb_with_date_and_month`,
        {
          method: "POST",
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ month, fromDate, toDate }),
        },
      );

      if (!response.ok) throw new Error("Failed to fetch data");

      const result = await response.json();
      const students = result.data || [];

      if (students.length === 0) {
        toast.error("No records found for given filters.");
        return;
      }

      const formattedData = students.map((item) => ({
        "Admission No": item.studentDetails?.admission_no,
        Name: item.studentDetails?.name,
        Class: item.studentDetails?.currentClass,
        Section: item.studentDetails?.currentSection,
        "Roll No": item.studentDetails?.rollNo,
        "Phone No": item.studentDetails?.phoneNo,
        Gender: item.studentDetails?.gender,
        "Scholarship Type": item.scholarshipType,
        "Monthly Fee": item.monthlyFee,
        "Transport Fee": item.transportFee,
        "Paid Month": item.paidMonth?.month,
        "Receipt No": item.paidMonth?.monthlyFee?.receiptNo,
        "Paid Date": item.paidMonth?.monthlyFee?.date,
        "Payment Mode": Object.keys(
          item.paidMonth?.monthlyFee?.paymentMode || {},
        )[0],
        "Total Amount": item.paidMonth?.TotalAmt,
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Paid Fee Report");

      const today = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `PaidFee_DateMonthWise_${month}_${today}.xlsx`);
      toast.success("Report Generated");
    } catch (error) {
      toast.error("Error generating report");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div className="">
            <div class="container-fluid my-3 px-3">
              <div className="row g-2">
                <div className="col-lg-8">
                  <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table class="table mb-0 text-nowrap table-centered">
                      <thead class="table-light">
                        <tr>
                          <th className="w-10">No.</th>
                          <th className="lh-1 w-60">
                            Details
                            <i className="bi bi-info-circle ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Displaying Information as per Record
                            </span>
                          </th>
                          <th className="lh-1 w-30">
                            Action
                            <i className="bi bi-person-gear ms-2 text-dark"></i>
                            <br />
                            <span className="fs-8 fw-normal text-secondary">
                              Role-Specific Action Panel
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRecords.map((student, index) => {
                          const paidMonths = student.paidMonths || [];
                          const allMonths = monthsList.slice(0, 12);
                          const unpaidMonths = allMonths.filter(
                            (month) => !paidMonths.includes(month),
                          );

                          const sortedPaidMonths = paidMonths
                            .slice()
                            .sort(
                              (a, b) =>
                                monthsList.indexOf(a) - monthsList.indexOf(b),
                            );

                          const groupedPaidMonths = sortedPaidMonths.reduce(
                            (acc, month, idx) => {
                              const groupIndex = Math.floor(idx / 5);
                              if (!acc[groupIndex]) acc[groupIndex] = [];
                              acc[groupIndex].push(month);
                              return acc;
                            },
                            [],
                          );

                          const monthlyFee = student.monthlyFee || 0;
                          const transportFee = student.transportFee || 0;

                          const monthCount = unpaidMonths.length;
                          const transportableMonths = unpaidMonths.filter(
                            (m) => m !== "June",
                          ).length;

                          const totalDue =
                            monthlyFee * monthCount +
                            transportFee * transportableMonths;

                          return (
                            <tr key={index}>
                              <td>{indexOfFirstRecord + index + 1}.</td>
                              <td className="lh-sm py-4 text-wrap">
                                <span className="text-dark">Student ID</span>{" "}
                                &mdash; {student.studentDetails.studentId}
                                <br />
                                <span className="text-dark">
                                  Admission No
                                </span>{" "}
                                &mdash; {student.studentDetails.admission_no}
                                <br />
                                <span className="text-dark">
                                  Student Name
                                </span>{" "}
                                &mdash; {student.studentDetails.name}
                                <br />
                                <span className="text-dark">
                                  Class
                                </span> &mdash;{" "}
                                {student.studentDetails.currentClass}
                                <br />
                                <span className="text-dark">Section</span>{" "}
                                &mdash; {student.studentDetails.currentSection}
                                <br />
                                <span className="text-dark">Roll No</span>{" "}
                                &mdash; {student.studentDetails.rollNo}
                                <br />
                                <span className="text-dark">Gender</span>{" "}
                                &mdash; {student.studentDetails.gender}
                                <br />
                                <span className="text-dark">Phone No</span>{" "}
                                &mdash; {student.studentDetails.phoneNo}
                                <br />
                                <span className="text-dark">
                                  Scholarship Type
                                </span>{" "}
                                &mdash;{" "}
                                {capitalizeWords(student.scholarshipType)}
                                <hr className="w-25" />
                                <span className="text-dark">Stoppage</span>{" "}
                                &mdash;{" "}
                                <span className="text-capitalize">
                                  {student.transportFacility.busStop || "NONE"}
                                </span>
                                <br />
                                <span className="text-dark">
                                  Vehicle No
                                </span>{" "}
                                &mdash;{" "}
                                {student.transportFacility.vehicleNo || "N/A"}
                                <br />
                                <span className="text-dark">Bus No</span>{" "}
                                &mdash;{" "}
                                {student.transportFacility.busNumber || "N/A"}
                                <hr className="w-25" />
                                <span className="text-dark">
                                  Paid Months
                                </span>{" "}
                                &mdash;{" "}
                                {groupedPaidMonths.map((group, i) => (
                                  <span key={i}>
                                    {group.join(", ")}
                                    <br />
                                  </span>
                                ))}
                                <br />
                                <span className="text-dark">
                                  Monthly Fee
                                </span>{" "}
                                &mdash; {monthlyFee}
                                {transportFee !== 0 && (
                                  <>
                                    <br />
                                    <span className="text-dark">
                                      Transport Fee
                                    </span>{" "}
                                    &mdash; {transportFee}
                                  </>
                                )}
                                <br />
                                <span className="text-dark">
                                  Total Amount Paid
                                </span>{" "}
                                &mdash; {totalDue}
                              </td>
                              <td>
                                <div className="d-flex flex-column gap-1 width-fit-content">
                                  <Link
                                    to="/view-student"
                                    state={{
                                      admissionNo:
                                        student.studentDetails.admission_no,
                                    }}
                                    className="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                  >
                                    View Details
                                  </Link>
                                  <Link
                                    to="/collect-fees"
                                    state={{
                                      admissionNo:
                                        student.studentDetails.admission_no,
                                    }}
                                    className="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                  >
                                    Fee Ledger
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="col-lg-4">
                  <div class="">
                    <div className="mb-3">
                      <div className="bg-white rounded-pill">
                        <form
                          className="d-flex align-items-center"
                          onSubmit={(e) => e.preventDefault()}
                        >
                          <span className="position-absolute ps-3 search-icon">
                            <i className="bi bi-filter-circle-fill ms-1" />
                          </span>
                          <input
                            type="search"
                            className="form-control ps-7 rounded-3 border-0 rounded-4"
                            placeholder="Filter Data"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </form>
                      </div>
                      <div className="mt-2 ms-4">
                        <nav aria-label="breadcrumb">
                          <ol class="breadcrumb pe-3">
                            <li class="breadcrumb-item">
                              <a href="#">Fees Collection</a>
                            </li>
                            <li
                              class="breadcrumb-item active"
                              aria-current="page"
                            >
                              Paid Fee
                            </li>
                          </ol>
                        </nav>
                      </div>
                    </div>

                    <div className="p-4 bg-white rounded-4">
                      <div class="bg-danger-soft rounded-4 p-3 d-flex align-items-center">
                        <div class="avatar-group">
                          {[1, 2, 3, 4, 5].map((num) => (
                            <span className="avatar avatar-md" key={num}>
                              <img
                                alt={`avatar-${num}`}
                                src={`/assets/images/students/${num}.jpg`}
                                className="rounded-circle"
                              />
                            </span>
                          ))}
                        </div>
                        <h3 className="fw-bold m-0 text-center fs-4 ms-3">
                          {filteredRecords.length.toLocaleString("en-IN")}
                        </h3>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="d-flex align-items-center">
                          {loading ? (
                            <div
                              className="spinner-border text-dark data-loader-spinner"
                              role="status"
                            ></div>
                          ) : (
                            <img src="/assets/images/folder.svg" />
                          )}

                          <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                            Paid Fee
                            <br />
                            <span className="text-secondary fs-6 fw-medium lh-sm">
                              1.4 MB Size
                            </span>
                          </p>
                        </div>
                        <span
                          onClick={handleExportExcel}
                          className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer"
                        >
                          Export
                        </span>
                      </div>

                      <div class="px-4 py-2 mt-3 bg-light rounded-4">
                        <li className="list-group-item px-0">
                          <a
                            className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                            data-bs-toggle="collapse"
                            href="#specifications"
                            role="button"
                            aria-expanded="false"
                            aria-controls="specifications"
                          >
                            <p className="m-0 fw-medium lh-sm text-dark">
                              Download Report
                            </p>
                            <span className="chevron-arrow ms-4">
                              <i className="bi bi-chevron-down fs-4" />
                            </span>
                          </a>
                          <div
                            className="collapse"
                            id="specifications"
                            data-bs-parent="#ecommerceAccordion"
                          >
                            <div className="py-3">
                              <div class="rounded-4 d-flex flex-wrap">
                                <span
                                  data-bs-toggle="modal"
                                  data-bs-target="#classSectionMonthWiseModal"
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Class/Section Month Wise
                                </span>
                                <span
                                  data-bs-toggle="modal"
                                  data-bs-target="#dateMonthWiseModal"
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Date & Month Wise
                                </span>
                                <span
                                  onClick={handleExportClassWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Class/Section Wise
                                </span>
                                <span
                                  onClick={handleExportGenderWiseExcel}
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Gender Wise
                                </span>
                                <span
                                  onClick={
                                    handleExportAvailingTransportWiseExcel
                                  }
                                  class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1"
                                >
                                  Transport Availing
                                </span>
                              </div>
                            </div>
                          </div>
                        </li>
                      </div>

                      {paidFeeDetails.length > 0 && (
                        <>
                          <div className="position-relative my-3">
                            <span
                              className="circle-slider-button bg-dark rounded-circle position-absolute start-0 top-50 translate-middle-y z-1 cursor-pointer"
                              onClick={scrollLeft}
                            >
                              <i className="bi bi-chevron-left text-white"></i>
                            </span>
                            <div
                              className="gap-1 d-flex overflow-x-auto px-6"
                              ref={scrollContainerRef}
                              style={{
                                scrollBehavior: "smooth",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <span
                                className={`btn btn-tag btn-sm ${
                                  selectedMonth === "All"
                                    ? "bg-success-subtle border border-success text-success-emphasis"
                                    : ""
                                }`}
                                onClick={() => setSelectedMonth("All")}
                              >
                                All
                              </span>
                              {monthsList.slice(0, 12).map((month) => (
                                <span
                                  key={month}
                                  className={`btn btn-tag btn-sm ${
                                    selectedMonth === month
                                      ? "bg-success-subtle border border-success text-success-emphasis"
                                      : ""
                                  }`}
                                  onClick={() => setSelectedMonth(month)}
                                >
                                  {month}
                                </span>
                              ))}
                            </div>
                            <span
                              className="circle-slider-button bg-dark rounded-circle position-absolute end-0 top-50 translate-middle-y z-1 cursor-pointer"
                              onClick={scrollRight}
                            >
                              <i className="bi bi-chevron-right text-white"></i>
                            </span>
                          </div>

                          <div className="position-relative mt-3 mb-2">
                            <span
                              className="circle-slider-button bg-dark rounded-circle position-absolute start-0 top-50 translate-middle-y z-1 cursor-pointer"
                              onClick={scrollLeftClass}
                            >
                              <i className="bi bi-chevron-left text-white"></i>
                            </span>
                            <div
                              className="gap-1 d-flex overflow-x-auto px-6"
                              ref={scrollClassRef}
                              style={{
                                scrollBehavior: "smooth",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <span
                                className={`btn btn-tag btn-sm ${
                                  selectedClass === "All"
                                    ? "bg-success-subtle border border-success text-success-emphasis"
                                    : ""
                                }`}
                                onClick={() => setSelectedClass("All")}
                              >
                                All
                              </span>
                              {allClasses.map((cls) => (
                                <span
                                  key={cls}
                                  className={`btn btn-tag btn-sm ${
                                    selectedClass === cls
                                      ? "bg-success-subtle border border-success text-success-emphasis"
                                      : ""
                                  }`}
                                  onClick={() => setSelectedClass(cls)}
                                >
                                  {cls}
                                </span>
                              ))}
                            </div>
                            <span
                              className="circle-slider-button bg-dark rounded-circle position-absolute end-0 top-50 translate-middle-y z-1 cursor-pointer"
                              onClick={scrollRightClass}
                            >
                              <i className="bi bi-chevron-right text-white"></i>
                            </span>
                          </div>

                          <div className="position-relative mb-3">
                            <span
                              className="circle-slider-button bg-dark rounded-circle position-absolute start-0 top-50 translate-middle-y z-1 cursor-pointer"
                              onClick={scrollLeftSection}
                            >
                              <i className="bi bi-chevron-left text-white"></i>
                            </span>
                            <div
                              className="gap-1 d-flex overflow-x-auto px-6"
                              ref={scrollSectionRef}
                              style={{
                                scrollBehavior: "smooth",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <span
                                className={`btn btn-tag btn-sm ${
                                  selectedSection === "All"
                                    ? "bg-success-subtle border border-success text-success-emphasis"
                                    : ""
                                }`}
                                onClick={() => setSelectedSection("All")}
                              >
                                All
                              </span>
                              {allSections.map((sec) => (
                                <span
                                  key={sec}
                                  className={`btn btn-tag btn-sm ${
                                    selectedSection === sec
                                      ? "bg-success-subtle border border-success text-success-emphasis"
                                      : ""
                                  }`}
                                  onClick={() => setSelectedSection(sec)}
                                >
                                  {sec}
                                </span>
                              ))}
                            </div>
                            <span
                              className="circle-slider-button bg-dark rounded-circle position-absolute end-0 top-50 translate-middle-y z-1 cursor-pointer"
                              onClick={scrollRightSection}
                            >
                              <i className="bi bi-chevron-right text-white"></i>
                            </span>
                          </div>

                          <div class="mt-1">
                            <div className="row g-1">
                              <div className="col-6">
                                <div className="p-3 bg-info-soft rounded-4">
                                  <h4 className="fw-bold m-0 text-center fs-4">
                                    {totalMonthlyDue.toLocaleString("en-IN")}
                                  </h4>
                                  <p className="text-center fs-5 m-0 lh-sm">
                                    Total Monthly Fee
                                  </p>
                                </div>
                              </div>
                              <div className="col-6">
                                <div className="p-3 bg-info-soft rounded-4">
                                  <h4 className="fw-bold m-0 text-center fs-4">
                                    {totalTransportDue.toLocaleString("en-IN")}
                                  </h4>
                                  <p className="text-center fs-5 m-0 lh-sm">
                                    Total Transport Fee
                                  </p>
                                </div>
                              </div>
                              <div className="col-6">
                                <div className="p-3 bg-success-soft rounded-4">
                                  <h4 className="fw-bold m-0 text-center fs-4">
                                    {totalDueAllStudents.toLocaleString(
                                      "en-IN",
                                    )}
                                  </h4>
                                  <p className="text-center fs-5 m-0 lh-sm">
                                    Total Amount Paid
                                  </p>
                                </div>
                              </div>
                              <div className="col-6">
                                <div className="p-3 bg-danger-soft rounded-4">
                                  <h4 className="fw-bold m-0 text-center fs-4">
                                    {totalDueOfSiblingStudents.toLocaleString(
                                      "en-IN",
                                    )}
                                  </h4>
                                  <p className="text-center fs-5 m-0 lh-sm">
                                    From Sibling Students
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="my-4">
                      <nav>
                        <ul className="pagination justify-content-center mb-0">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={prevPage}>
                              <i className="bi bi-chevron-left"></i>
                            </button>
                          </li>

                          {(() => {
                            const pageNumbers = [];
                            pageNumbers.push(1);

                            if (currentPage > 3) {
                              pageNumbers.push("left-ellipsis");
                            }

                            if (
                              currentPage !== 1 &&
                              currentPage !== totalPages
                            ) {
                              pageNumbers.push(currentPage);
                            }

                            if (currentPage < totalPages - 2) {
                              pageNumbers.push("right-ellipsis");
                            }

                            if (totalPages > 1) {
                              pageNumbers.push(totalPages);
                            }

                            return pageNumbers.map((page, index) => {
                              if (
                                page === "left-ellipsis" ||
                                page === "right-ellipsis"
                              ) {
                                return (
                                  <li
                                    key={index}
                                    className="page-item disabled"
                                  >
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }

                              return (
                                <li
                                  key={index}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => setCurrentPage(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              );
                            });
                          })()}

                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button className="page-link" onClick={nextPage}>
                              <i className="bi bi-chevron-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>

                  <div
                    class="modal m-0 fade"
                    id="classSectionMonthWiseModal"
                    tabindex="-1"
                    aria-labelledby="classSectionMonthWiseModalLabel"
                    aria-hidden="true"
                  >
                    <div class="modal-dialog modal-dialog-centered">
                      <div class="modal-content rounded-5 overflow-hidden">
                        <div class="p-4 bg-danger-soft d-flex justify-content-end">
                          <i
                            className="bi bi-x-lg text-dark fs-3 cursor-pointer position-relative z-1"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                          ></i>
                        </div>
                        <div class="pt-0 p-5">
                          <div className="d-flex justify-content-center mt-n8">
                            <img
                              src="/assets/images/report.jpg"
                              width="90"
                              className="object-fit-contain p-3 rounded-5 bg-white"
                            />
                          </div>

                          <div className="mt-2">
                            <h5 class="text-center lh-1 fw-medium">
                              Class / Section Month Wise Report
                              <br />
                              <span className="text-secondary">
                                Generate Report
                              </span>
                            </h5>
                          </div>

                          <div className="mt-4">
                            <div className="bg-light rounded-pill py-2 mb-3">
                              <h5 class="fs-5 text-center m-0 lh-1 fw-medium">
                                Select Month &nbsp;(Student's Fee Paid Upto)
                              </h5>
                            </div>

                            <div className="d-flex flex-wrap justify-content-center gap-1 px-1">
                              {monthList.map((month, index) => (
                                <div
                                  key={index}
                                  className={`bg-dark-soft rounded-pill cursor-pointer px-3 py-1 fs-6 text-capitalize ${
                                    selectedMonthForReport === month
                                      ? "bg-warning text-white border-warning"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    setSelectedMonthForReport(month)
                                  }
                                >
                                  {month}
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="mt-5 d-flex justify-content-center flex-column align-items-center">
                            <div>
                              <button
                                onClick={() =>
                                  handleExportClassMonthWiseExcel(
                                    selectedMonthForReport,
                                  )
                                }
                                class="btn btn-primary rounded-pill px-6 fs-5 fw-medium"
                              >
                                Download Report
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    class="modal m-0 fade"
                    id="dateMonthWiseModal"
                    tabindex="-1"
                    aria-labelledby="dateMonthWiseModalLabel"
                    aria-hidden="true"
                  >
                    <div class="modal-dialog modal-dialog-centered">
                      <div class="modal-content rounded-5 overflow-hidden">
                        <div class="p-4 bg-danger-soft d-flex justify-content-end">
                          <i
                            className="bi bi-x-lg text-dark fs-3 cursor-pointer position-relative z-1"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                          ></i>
                        </div>
                        <div class="pt-0 p-5">
                          <div className="d-flex justify-content-center mt-n8">
                            <img
                              src="/assets/images/report.jpg"
                              width="90"
                              className="object-fit-contain p-3 rounded-5 bg-white"
                            />
                          </div>

                          <div className="mt-2">
                            <h5 class="text-center lh-1 fw-medium">
                              Date & Month Wise Report
                              <br />
                              <span className="text-secondary">
                                Generate Report
                              </span>
                            </h5>
                          </div>

                          <div className="mt-4">
                            <div className="bg-light rounded-pill py-2 mb-3">
                              <h5 class="fs-5 text-center m-0 lh-1 fw-medium">
                                Select Date & Month &nbsp;(Fee Paid Between)
                              </h5>
                            </div>

                            <div className="row g-2 mb-3 pb-1">
                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  From Date{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <input
                                  type="date"
                                  class="form-control text-dark"
                                  name="fromDate"
                                  value={fromDate}
                                  onChange={(e) => setFromDate(e.target.value)}
                                  required
                                />
                              </div>
                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  To Date <span className="text-danger">*</span>
                                </label>
                                <input
                                  type="date"
                                  class="form-control text-dark"
                                  name="toDate"
                                  value={toDate}
                                  onChange={(e) => setToDate(e.target.value)}
                                  required
                                />
                              </div>
                            </div>

                            <div className="d-flex flex-wrap justify-content-center gap-1 px-1">
                              {monthList.map((month, index) => (
                                <div
                                  key={index}
                                  className={`bg-dark-soft rounded-pill cursor-pointer px-3 py-1 fs-6 text-capitalize ${
                                    selectedMonthForReport === month
                                      ? "bg-warning text-white border-warning"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    setSelectedMonthForReport(month)
                                  }
                                >
                                  {month}
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="mt-5 d-flex justify-content-center flex-column align-items-center">
                            <div>
                              {downloading ? (
                                <div class="bg-primary md-circle-button fw-medium">
                                  <div
                                    class="spinner-border spinner-border-md text-white"
                                    role="status"
                                  ></div>
                                </div>
                              ) : (
                                <button
                                  onClick={() =>
                                    handleExportdateMonthWiseExcel(
                                      selectedMonthForReport,
                                      fromDate,
                                      toDate,
                                    )
                                  }
                                  className="btn btn-primary rounded-pill px-6 fs-5 fw-medium"
                                  disabled={downloading}
                                >
                                  Download Report
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default PaidFee;
